// Les cotes d'un contour de pièce, pour l'outil (RoomCanvas) et le dossier
// client (ProDossier) : la même règle des deux côtés.
//
// Une longueur de mur se lit HORS de la pièce, au milieu du mur, décalée vers
// l'extérieur ; le texte reste horizontal (une cote inclinée se lit mal en
// petit) et s'ancre du côté où il y a de la place. La surface se pose au
// centre de gravité de la surface — pas à la moyenne des sommets, qui sort
// d'une pièce en L. Avant : chaque cote était posée sur le trait, quelle que
// soit l'orientation du mur, et le total au milieu chevauchait les cotes des
// pièces étroites.

/** Aire signée (shoelace) : positive si les sommets tournent dans le sens des y croissants. */
export function aireSignee(v) {
  let a = 0;
  for (let i = 0; i < v.length; i++) { const j = (i + 1) % v.length; a += v[i].x * v[j].y - v[j].x * v[i].y; }
  return a / 2;
}

/** Centre de gravité de la surface ; moyenne des sommets si le contour est dégénéré. */
export function centroide(v) {
  const n = v.length;
  if (!n) return { x: 0, y: 0 };
  const a = aireSignee(v);
  if (Math.abs(a) < 1e-6) return { x: v.reduce((s, p) => s + p.x, 0) / n, y: v.reduce((s, p) => s + p.y, 0) / n };
  let cx = 0, cy = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const k = v[i].x * v[j].y - v[j].x * v[i].y;
    cx += (v[i].x + v[j].x) * k; cy += (v[i].y + v[j].y) * k;
  }
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

/**
 * Les cotes à dessiner, en coordonnées SVG.
 * @param {Array<{x,y}>} vertices  contour en mètres
 * @param {Function} toSvg         mètres → pixels
 * @param {object} o
 * @param {number} o.ecart   distance du trait au texte, en pixels
 * @param {number} o.minLen  mur plus court (en mètres) : pas de cote
 * @param {number} o.minPx   mur plus court à l'écran (en pixels) : pas de cote, il n'y a pas la place
 * @returns {Array<{ i, x, y, len, anchor, baseline }>} anchor = text-anchor, baseline = dominant-baseline
 */
export function cotes(vertices, toSvg, { ecart = 12, minLen = 0.15, minPx = 0 } = {}) {
  const n = vertices.length;
  if (n < 2) return [];
  const cs = toSvg(centroide(vertices));
  const out = [];
  for (let i = 0; i < n; i++) {
    const a = vertices[i], b = vertices[(i + 1) % n];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < minLen) continue;
    const A = toSvg(a), B = toSvg(b);
    const px = Math.hypot(B.x - A.x, B.y - A.y);
    if (px < minPx || px < 1) continue;
    const mid = { x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 };
    // Normale unitaire au mur, tournée vers l'extérieur : à l'opposé du centre.
    let nx = -(B.y - A.y) / px, ny = (B.x - A.x) / px;
    if ((mid.x - cs.x) * nx + (mid.y - cs.y) * ny < 0) { nx = -nx; ny = -ny; }
    const horizontal = Math.abs(nx) < 0.5;             // mur ± horizontal : cote au-dessus ou au-dessous
    out.push({
      i, len,
      x: mid.x + nx * ecart, y: mid.y + ny * ecart,
      anchor: horizontal ? 'middle' : (nx > 0 ? 'start' : 'end'),
      baseline: horizontal ? (ny > 0 ? 'hanging' : 'auto') : 'middle',
    });
  }
  return out;
}

/** Attributs d'un texte SVG à halo blanc : lisible sur un trait, une grille, un fond coloré. */
export const HALO = { paintOrder: 'stroke', stroke: '#fff', strokeWidth: 3, strokeLinejoin: 'round' };
